import { SidebarPortal } from '@plone/volto/components';
import { withBlockExtensions } from '@plone/volto/helpers';
import config from '@plone/volto/registry';
import cx from 'classnames';
import { useEffect, useRef, useState } from 'react';
import ContentEditable from 'react-contenteditable';
import HeadingSidebar from './Sidebar';

// Source: https://stackoverflow.com/questions/5796718/html-entity-decode
var decodeHTMLEntities = (function () {
  if (typeof window === 'undefined') {
    return () => {
      throw new Error('DecodeHTMLEntities is not supported in SSR.');
    };
  }
  // closure to avoid creating the object each time
  var element = document.createElement('div');

  function decodeHTMLEntities(str: string): string {
    if (str && typeof str === 'string') {
      // strip script/html tags
      str = str.replace(/<script[^>]*>([\S\s]*?)<\/script>/gim, '');
      str = str.replace(/<\/?\w(?:[^"'>]|"[^"]*"|'[^']*')*>/gim, '');
      element.innerHTML = str;
      str = element.textContent || '';
      element.textContent = '';
    }

    return str;
  }

  return decodeHTMLEntities;
})();

interface HeadingEditProps {
  block: string;
  data: {
    heading?: string;
    tag?: string;
    alignment?: string;
    showUnderline?: boolean;
  };
  onChangeBlock: (block: string, data: any) => void;
  onSelectBlock: (block: string) => void;
  onAddBlock: (type: string, index: number) => string;
  onFocusPreviousBlock: (block: string, node: any) => void;
  onFocusNextBlock: (block: string, node: any) => void;
  selected: boolean;
  index: number;
  [key: string]: any; // Add index signature for any other props
}

const HeadingEdit = (props: HeadingEditProps) => {
  const {
    block,
    data,
    onChangeBlock,
    onSelectBlock,
    onAddBlock,
    onFocusPreviousBlock,
    onFocusNextBlock,
    selected,
    index,
  } = props;

  const [html, setHtml] = useState(data.heading || '');
  const editable = useRef<any>(null);

  useEffect(() => {
    if (selected && editable.current) {
      editable.current.focus();
    }
  }, [selected]);

  const handleChange = (event: any) => {
    // Strip all html tags and decode html entities. Apparently this is
    // necessary.
    const cleanedText = decodeHTMLEntities(event.target.value);

    if (event.nativeEvent.inputType === 'insertFromPaste') {
      setHtml(cleanedText);
    } else {
      setHtml(event.target.value);
    }

    onChangeBlock(block, { ...data, heading: cleanedText });
  };

  const show_alignment =
    config.blocks?.blocksConfig?.junts_heading?.show_alignment;

  return (
    <div className="block">
      <div
        className={cx('junts-heading-wrapper', {
          'junts-heading-wrapper-underline': data.showUnderline,
        })}
      >
        <ContentEditable
          innerRef={editable}
          className="editable"
          style={show_alignment ? { textAlign: data.alignment } : {}}
          tagName={data.tag ?? 'h2'}
          html={html} // innerHTML of the editable div
          onChange={handleChange} // handle innerHTML change
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
              onSelectBlock(
                onAddBlock(config.settings.defaultBlockType, index + 1),
              );
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              e.stopPropagation();
              onFocusPreviousBlock(block, editable.current);
            } else if (e.key === 'ArrowDown') {
              e.preventDefault();
              e.stopPropagation();
              onFocusNextBlock(block, editable.current);
            }
          }}
        />
        {/* @ts-ignore - Ignore TS errors for SidebarPortal component */}
        <SidebarPortal selected={selected}>
          <HeadingSidebar
            {...props}
            data={data}
            block={block}
            onChangeBlock={onChangeBlock}
          />
        </SidebarPortal>
      </div>
    </div>
  );
};

export default withBlockExtensions(HeadingEdit);
